import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import React, { useMemo, useState, useCallback } from "react";
import { PrimaryButton, SecondaryButton } from "../Button";
import { DimmedActionLink } from "../Link";
import RichContent, { countCharacters, hasTable, isContentEmpty, shortenContent } from "../RichContent";
import { Editor, MentionedPersonLookupFn, useEditor } from "../RichEditor";
import type { RichTextHandlers } from "../RichContent/types";
import { RichEditorHandlers } from "../RichEditor/useEditor";
import { hasTaskList, type TaskListInteraction } from "../RichEditor/taskLists";

const PREVIEW_CHARACTER_LIMIT = 450;

interface Props {
  description: any;
  onDescriptionChange: (newDescription: any) => Promise<boolean>;
  richTextHandlers: RichTextHandlers;
  label: string;
  canEdit?: boolean;
  placeholder?: string;
  zeroStatePlaceholder?: string;
  testId?: string;
  emptyTestId?: string;
  localDraftKey?: string;
}

export function PageDescription({
  description,
  onDescriptionChange,
  richTextHandlers,
  canEdit,
  label,
  placeholder = i18n.t("Describe..."),
  zeroStatePlaceholder = i18n.t("Add notes..."),
  testId,
  emptyTestId,
  localDraftKey,
}: Props) {
  useTranslation();
  const descriptionIsEmpty = isContentEmpty(description);
  const initialMode = descriptionIsEmpty ? "zero" : "view";
  const [mode, setMode] = useState<"view" | "edit" | "zero">(initialMode);

  React.useEffect(() => {
    if (mode === "edit") return;

    if (descriptionIsEmpty && mode === "view") {
      setMode("zero");
    } else if (!descriptionIsEmpty && mode === "zero") {
      setMode("view");
    }
  }, [descriptionIsEmpty, mode]);

  const startEdit = () => setMode("edit");

  if (mode === "zero") {
    return (
      <ZeroState startEdit={startEdit} canEdit={canEdit} placeholder={zeroStatePlaceholder} testId={emptyTestId} />
    );
  }

  return (
    <div data-test-id={testId}>
      <SectionHeader title={label} startEdit={startEdit} showButtons={canEdit && mode !== "edit"} />

      {mode === "view" && (
        <ViewMode
          rawDescription={description}
          mentionedPersonLookup={richTextHandlers.mentionedPersonLookup}
          taskList={richTextHandlers.taskList}
        />
      )}
      {mode === "edit" && (
        <EditMode
          description={description}
          richTextHandlers={richTextHandlers}
          onDescriptionChange={onDescriptionChange}
          setMode={setMode}
          placeholder={placeholder}
          localDraftKey={localDraftKey}
        />
      )}
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  startEdit: () => void;
  showButtons?: boolean;
}

function SectionHeader({ title, startEdit, showButtons }: SectionHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <h2 className="font-bold">{title}</h2>
      {showButtons && (
        <SecondaryButton size="xxs" onClick={startEdit} testId="edit-description">
          {t("Edit")}
        </SecondaryButton>
      )}
    </div>
  );
}

interface ViewModeProps {
  rawDescription: any;
  mentionedPersonLookup: MentionedPersonLookupFn;
  taskList: TaskListInteraction;
}

function ViewMode({ rawDescription, mentionedPersonLookup, taskList }: ViewModeProps) {
  const { t } = useTranslation();
  const { transformContent, length, isExpanded, toggleExpand } = useExpandDescription(rawDescription);
  const canCollapse = length > PREVIEW_CHARACTER_LIMIT && !hasTaskList(rawDescription);

  return (
    <div className="mt-2">
      <RichContent
        content={rawDescription}
        transformContent={transformContent}
        mentionedPersonLookup={mentionedPersonLookup}
        taskList={taskList}
        className={canCollapse && !isExpanded && hasTable(rawDescription) ? "max-h-96 overflow-hidden" : undefined}
      />

      {canCollapse && (
        <DimmedActionLink
          aria-expanded={isExpanded}
          onClick={toggleExpand}
          className="text-sm mt-1 font-medium"
          underline="hover"
          disableColorHoverEffect
        >
          {isExpanded ? t("Collapse") : t("Expand")}
        </DimmedActionLink>
      )}
    </div>
  );
}

interface EditModeProps {
  description: any;
  richTextHandlers: RichEditorHandlers;
  onDescriptionChange: (newDescription: any) => Promise<boolean>;
  setMode: React.Dispatch<React.SetStateAction<"view" | "edit" | "zero">>;
  placeholder: string;
  localDraftKey?: string;
}

function EditMode({
  description,
  richTextHandlers,
  onDescriptionChange,
  setMode,
  placeholder,
  localDraftKey,
}: EditModeProps) {
  const { t } = useTranslation();
  const editor = useEditor({
    content: description,
    editable: true,
    placeholder,
    handlers: richTextHandlers,
    autoFocus: true,
    localDraft: { key: localDraftKey },
  });

  const save = useCallback(async () => {
    const content = editor.getJson();
    const success = await onDescriptionChange(content);

    if (success) {
      editor.clearLocalDraft();

      if (isContentEmpty(content)) {
        setMode("zero");
      } else {
        setMode("view");
      }
    }
  }, [editor, setMode, onDescriptionChange]);

  const cancel = useCallback(() => {
    editor.clearLocalDraft();
    setMode(isContentEmpty(description) ? "zero" : "view");
  }, [editor, setMode, description]);

  return (
    <div className="mt-2">
      <Editor editor={editor} />
      <div className="flex gap-2 mt-2">
        <PrimaryButton size="xs" onClick={save}>
          {t("Save")}
        </PrimaryButton>
        <SecondaryButton size="xs" onClick={cancel}>
          {t("Cancel")}
        </SecondaryButton>
      </div>
    </div>
  );
}

interface ZeroStateProps {
  startEdit: () => void;
  canEdit?: boolean;
  placeholder: string;
  testId?: string;
}

function ZeroState({ startEdit, canEdit, placeholder, testId }: ZeroStateProps) {
  if (!canEdit) return null;

  return (
    <div data-test-id={testId}>
      <button
        onClick={startEdit}
        // The py-4 -my-4 is to make the clickable are larger without adding extra empty space
        className="py-4 -my-4 w-full text-left text-content-dimmed hover:text-content-base text-sm transition-colors cursor-pointer"
      >
        {placeholder}
      </button>
    </div>
  );
}

function useExpandDescription(rawDescription: any) {
  const [isExpanded, setIsExpanded] = useState(false);

  const length = useMemo(() => {
    return rawDescription ? countCharacters(rawDescription, { skipParse: true }) : 0;
  }, [rawDescription]);

  const transformContent = useCallback(
    (content: any) => {
      if (length <= PREVIEW_CHARACTER_LIMIT || isExpanded || hasTaskList(content) || hasTable(content)) return content;
      return shortenContent(content, PREVIEW_CHARACTER_LIMIT, { suffix: "...", skipParse: true });
    },
    [length, isExpanded],
  );

  const toggleExpand = useCallback(() => setIsExpanded((prev) => !prev), [setIsExpanded]);

  return {
    transformContent,
    length,
    isExpanded,
    toggleExpand,
  };
}
